import { useMemo } from 'react';

import { Crumb } from '@/components/Breadcrumbs/BreadcrumbWrapper';
import { useAppSelector } from '@/redux/hooks';
import { getItemFacets } from '@/redux/modules/itemFacets';
import { getCategoryBrowsePath, getCategoryDimensionBrowsePath } from '@/utils/browsePath/itemFacetBrowsePath';
import { type CategoryLevel, getCategoryLevels } from '@/utils/categoryGrabber';

export const useItemPageCategoryBreadcrumbs = (itemId: number): Crumb[] => {
    const { categories, creators = [] } = useAppSelector((state) => getItemFacets(state, itemId));

    return useMemo(() => {
        const crumbs: Crumb[] = [];
        const creator = creators[0] || null;
        let categoryLevels: CategoryLevel[] = [];

        if (categories) {
            categoryLevels = getCategoryLevels(categories[0]);
        }

        for (let i = 0; i < categoryLevels.length; i += 1) {
            // `categoryUrl` is not used to build the link any more, but its presence is still the
            // signal that this level is a real, linkable category rather than a partially populated
            // one. Dropping it from the guard makes half-empty levels render as crumbs.
            if (categoryLevels[i].categoryName && categoryLevels[i].categoryUrl) {
                crumbs.push({
                    label: categoryLevels[i].categoryName,
                    link: getCategoryBrowsePath(categoryLevels[i].categoryName),
                });
            }
        }

        if (categoryLevels.length > 0 && creator) {
            const lastCategory = categoryLevels[categoryLevels.length - 1];
            if (lastCategory.categoryName && lastCategory.categoryUrl && creator.l1CategoryName) {
                crumbs.push({
                    label: `${creator.l1CategoryName} ${lastCategory.categoryName}`,
                    link: getCategoryDimensionBrowsePath(lastCategory.categoryName, 'creator', creator.l1CategoryName),
                });
            }
        }

        return crumbs;
    }, [categories, creators]);
};
